import React, { RefObject, useRef } from "react";
import styles from "./Tooltip.module.scss";
import {
  arrow,
  MiddlewareData,
  offset,
  Placement,
  shift,
  useFloating,
  autoUpdate
} from "@floating-ui/react-dom";
import clsx from "clsx";
import { Button } from "../Button/Button";

type Props = {
  onClose: React.MouseEventHandler<HTMLButtonElement>;
  referenceElement: RefObject<Element>;
  show: boolean;
  width?: number;
  arrowWidth?: number;
} & ( // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  | {}
  | {
      onFooterButtonClick: React.MouseEventHandler<HTMLButtonElement>;
      footerButtonLabel: string;
    }
);

export const Tooltip: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    children,
    onClose,
    referenceElement,
    show,
    width: _width,
    arrowWidth: _arrowWidth
  } = props;
  const width = _width ?? 224;
  const arrowWidth = _arrowWidth ?? 24;
  const arrowRef = useRef(null);
  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    elements: {
      reference: referenceElement.current
    },
    middleware: [
      offset({ mainAxis: arrowWidth / 2, crossAxis: -0.275 * width }),
      shift(),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  return (
    <div
      className={clsx({
        [styles.container]: true,
        [styles.show]: show
      })}
      ref={refs.setFloating}
      style={tooltipStyle(width, floatingStyles)}
      role="tooltip"
    >
      <div
        ref={arrowRef}
        className={styles.arrow}
        style={arrowStyle(arrowWidth, placement, middlewareData)}
      />
      <div className={styles.header}>
        <Button
          renderAs="button"
          variant="inline-text"
          size="small"
          iconName="close"
          dataContentPiece="tooltip_close"
          className={styles.closeButton}
          onClick={onClose}
        />
      </div>
      <div className={styles.content}>{children}</div>
      {"footerButtonLabel" in props && (
        <div className={styles.footer}>
          <Button
            renderAs="button"
            variant="inline-text"
            dataContentPiece="tooltip_footer_button"
            onClick={props.onFooterButtonClick}
          >
            {props.footerButtonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

const tooltipStyle = (width: number, floatingStyles: React.CSSProperties) => ({
  width,
  ...floatingStyles
});

const arrowStyle = (
  width: number,
  placement: Placement,
  middlewareData: MiddlewareData
) => {
  const squareWidth = Math.sqrt(Math.pow(width, 2) / 2);
  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }[placement.split("-")[0]];
  return {
    width: squareWidth,
    height: squareWidth,
    top: middlewareData.arrow?.y ?? "",
    left: middlewareData.arrow?.x ?? "",
    right: "",
    bottom: "",
    ...(staticSide ? { [staticSide]: `-${squareWidth / 2}px` } : {})
  };
};
